import React, { FC, useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { activeFilterIsStudentFilter } from '../../../../shared/components';
import { BookmarkModeEnum, showHelpModals, StudentFiltersEnum } from '../../../../shared/constants';
import TrajectoryToolboxButton, { TrajectoryToolboxButtonProps } from './TrajectoryToolboxButton';
import { useMediaQuery, useTheme } from '@material-ui/core';
import {
  TrajectorySectionCode,
  TrajectoryViewMode,
} from '../../../../../config/api/models/Trajectory';
import { useUser } from '../../../../shared';
import { BookWithChapters, UrlParams } from '../../../../shared/types';
import { LogActionTypes, useDataLogging } from '../../../../shared/data-logging';
import { TrajectoryToolboxMenu, TrajectoryToolboxWrapper } from './Atoms';
import { useParams } from 'react-router';
import TrajectoryControls from './TrajectoryControls';
import {
  SpriteIconsCustom,
  SpriteIconsLight,
  SpriteIconsRegular,
} from 'src/config/api/models/FontAwesome';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import {
  ToolboxPanelModeType,
  ToolboxPanelTypes,
  cleanPanel,
  setBlockToolboxPanel,
  setToolboxVisible,
} from 'src/modules/shared/slices/trajectoryToolbox/trajectoryToolbox';
import { TrajectoryToolboxNotesPanel } from './TrajectoryToolboxNotesPanel';
import { UserRoles } from 'src/config/api/models/User';
import {
  setActiveFilter,
  setSelectedGroup,
  setSelectedStudent,
} from 'src/modules/shared/slices/filters/entity/FiltersSlice';
import { setGeogebraAppName } from 'src/modules/shared/slices/geogebra/entity/geogebraSlice';
import {
  SidePanelModes,
  changePanelContent,
  openSidePanel,
} from 'src/modules/shared/slices/sidePanel/sidePanelSlice';
import { setSolutionsMode } from 'src/modules/shared/slices/solutions/entity/solutionsModeSlice';
import { getBlockNotes } from 'src/modules/shared/slices/notes/api/notes';
import {
  setCurrentStudent,
  setStudentMode,
} from 'src/modules/shared/slices/notes/entity/BlockNotesSlice';
import { ToolboxAction } from './types';
import {
  setBookmarkMode,
  setScroll,
} from 'src/modules/shared/slices/bookmark/entity/bookmarkEntity';
import DemoSnackBar, {
  ShowAlert,
  alertInitialState,
} from 'src/modules/shared/components/Snackbar/DemoSnackBar';
import DemoDialog, { ModalProps } from 'src/modules/app/components/Demo/DemoDialog';
import {
  setHasSeenBookmark,
  setHasSeenDownload,
  setHasSeenManual,
} from 'src/modules/shared/slices/ctas/CtasSlice';
import { isSchoolHours } from 'src/modules/shared/helpers/isSchoolHours';
import PrintPdf from './PrintPdf/PrintPdf';
import { setShowTrajectoryPrintPdfSlice } from 'src/modules/shared/slices/print/slice';
import {
  GetStudentBookmark,
  GetTeachersBookmarkByBook,
  useGetStudentBookmarkQuery,
  useGetTeacherBookmarksByBookQuery,
} from 'src/modules/shared/slices/bookmark/api/bookmarkApi';
import {
  setHandleDownArrow,
  setHandleUpArrow,
  setPageBlockIdToPresent,
  setShowPresentationMode,
} from 'src/modules/shared/slices/presentationMode/presentationModeSlice';
import useKeyBindings from 'src/modules/shared/hooks/useKeyBindings';
import { useDeviceType } from 'src/modules/shared/hooks/useDeviceType';
import { getBlocksToObserveList } from './helpers';
import useIntersectionObserver from 'src/modules/shared/hooks/useIntersectionObserver';
import { useFullScreen } from 'src/modules/shared/hooks/useFullScreen';

type Props = {
  mode: TrajectoryViewMode;
  sectionCode: TrajectorySectionCode;
  fullBook: BookWithChapters;
  isTrajectoryInDefaultBook?: boolean;
  onOpenSidebar: () => void;
  fullscreen: boolean;
  pageId: string | undefined;
};

export type TrajectoryToolboxMenuProps = {
  shouldRender: boolean;
  key: ToolboxAction;
  roles: string[];
} & TrajectoryToolboxButtonProps;

const env = process.env.REACT_APP_ENVIRONMENT;

const TrajectoryToolbox: FC<Props> = ({ sectionCode, fullBook, pageId = '' }) => {
  // hooks
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const log = useDataLogging();
  const { bookId, trajectoryId, chapterId, seriesId } = useParams<UrlParams>();
  const { user, isTeacher, isStudent, isAuthor, isSuperAdmin } = useUser();
  const deviceType = useDeviceType();

  // React
  const [showDownloadDialog, setShowDownloadDialog] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [showManualDialog, setShowManualDialog] = useState<boolean>(false);

  // redux
  const hasSeenBookmark = useAppSelector((state) => state.ctasSlice.hasSeenBookmark);
  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const toolboxPanelOpen = useAppSelector((state) => state.trajectoryToolbox.toolboxPanelOpen);
  const toolboxPanelMode = useAppSelector((state) => state.trajectoryToolbox.toolboxPanelMode);
  const toolboxPanelContent = useAppSelector(
    (state) => state.trajectoryToolbox.toolboxPanelContent
  );
  const isToolboxPanelBlocked = useAppSelector(
    (state) => state.trajectoryToolbox.isToolboxPanelBlocked
  );
  const geogebraAppName = useAppSelector((state) => state.geogebra.geogebraAppName);
  const fullscreenMode = useAppSelector((state) => state.fullscreenMode.fullscreenMode);
  const solutionsMode = useAppSelector((state) => state.solutionsMode.solutionsMode);
  const trajectory = useAppSelector((state) => state.trajectories.trajectory);
  const bookmarkMode = useAppSelector((state) => state.bookmark.bookmarkMode);
  const hasSeenDownload = useAppSelector((state) => state.ctasSlice.hasSeenDownload);
  const hasSeenManual = useAppSelector((state) => state.ctasSlice.hasSeenManual);
  const showTrajectoryPrintPdfSlice = useAppSelector(
    (state) => state.trajectoryPrintPdfSlice.showTrajectoryPrintPdfSlice
  );
  const showPresentationMode = useAppSelector(
    (state) => state.presentationMode.showPresentationMode
  );
  const blocks = useAppSelector((state) => state.blocks.blocks);
  const currentSectionCode = useAppSelector((state) => state.trajectories.currentSectionCode);

  const { data: notes } = getBlockNotes({ pageId }, { skip: isAuthor || !pageId });
  const { data: teacherBookmarks } = useGetTeacherBookmarksByBookQuery(bookId as string, {
    skip: !isTeacher,
  });
  const { data: studentBookmarksData } = useGetStudentBookmarkQuery(bookId as string, {
    skip: !isStudent,
  });

  const downloadModalProps: ModalProps = {
    title: 'Een nieuwe thuis voor de downloads!',
    hasSeen: () => dispatch(setHasSeenDownload(true)),
    body: [
      <>
        Met <strong>downloads die je in één klik ziet,</strong> motiveer jij leerlingen met een
        perfecte balans van digitaal en papier.
      </>,
    ],
    videoUrl: 'https://vimeo.com/913302238?share=copy',
    checkboxLabel: 'Verberg deze melding over de vernieuwde downloads',
    buttonText: 'NAAR DE HUIDIGE LERAARSKAMER',
    downloadUrl: fullBook.knowledgeBaseUrl,
    log: LogActionTypes.DOWNLOAD_WAS_SEEN_CTA,
  };

  const bookmarkModalProps: ModalProps = {
    title: 'Alle leerlingen onmiddellijk bij de juiste les',
    hasSeen: () => dispatch(setHasSeenBookmark(true)),
    body: [
      'Met de bladwijzer hou je bij waar je bleef met elke klas. Binnenkort kan jij bladwijzers      opslaan voor leerlingen. Met één klik op hun startpagina, zitten ze exact waar jij wil in de lesmethode.',
    ],
    videoUrl: 'https://vimeo.com/912503562',
    checkboxLabel: 'Verberg deze melding',
    buttonText: 'SLUIT',
    log: LogActionTypes.BOOKMARK_SEEN_CTA,
  };

  const teachersManualkModalProps: ModalProps = {
    title: 'Inspiratie in de leerkrachtenhandleiding',
    hasSeen: () => dispatch(setHasSeenManual(true)),
    body: [
      <>
        Hoe zouden de auteurs dit lestraject aanpakken? Dat lees je in de lerarenhandleiding.
        Beschikbaar <strong>1 september 2024</strong> voor leerjaar 1. (Leerjaren 2 t.e.m 6 op 1
        september 2025.)
      </>,
    ],
    videoUrl: 'https://vimeo.com/911146052?share=copy',
    checkboxLabel: 'Melding niet meer tonen',
    buttonText: 'SLUIT',
    log: LogActionTypes.PAPER_MANUAL_SEEN,
  };

  // mediaquery
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isSidePanelOpen = useAppSelector((state) => state.sidepanel.isOpen);
  const sidepanel = useAppSelector((state) => state.sidepanel.sidepanel);
  const sidePanelMode = useAppSelector((state) => state.sidepanel.sidePanelMode);

  const elementsToObserve: string[] = getBlocksToObserveList(blocks);

  const activeId = useIntersectionObserver(elementsToObserve, { threshold: 0.2 });

  useEffect(() => {
    if (!activeId) return;
    if (activeId) {
      let newId = null;
      newId = activeId?.replace('block_wrapper-', '');
      dispatch(setPageBlockIdToPresent(newId));
    }
  }, [activeId, dispatch]);

  useEffect(() => {
    if (!isSidePanelOpen) {
      dispatch(changePanelContent(SidePanelModes.NONE));
    }
  }, [dispatch, isSidePanelOpen]);

  useEffect(() => {
    return () => {
      dispatch(changePanelContent(SidePanelModes.NONE));
      dispatch(setBlockToolboxPanel(false));
    };
  }, [dispatch]);

  const handleOpenSidePanelWithNotes = useCallback(() => {
    if (sidePanelMode !== SidePanelModes.NOTES) {
      dispatch(changePanelContent(SidePanelModes.NOTES));
      dispatch(openSidePanel(true));
      log(LogActionTypes.SHOW_ALL_NOTES, {
        bookId,
        chapterId,
        trajectoryId,
        notesIds: notes?.map((note: any) => note.id),
      });
    } else {
      dispatch(changePanelContent(SidePanelModes.NONE));
      dispatch(openSidePanel(false));
    }
  }, [sidePanelMode, dispatch, log, bookId, chapterId, trajectoryId, notes]);

  const handleOpenSidePanelWithGeogebra = useCallback(
    (calculatorType: string) => {
      if (isSidePanelOpen && calculatorType === geogebraAppName) {
        dispatch(setGeogebraAppName(''));
        dispatch(openSidePanel(!isSidePanelOpen));
        dispatch(changePanelContent(SidePanelModes.NONE));
        return;
      }
      if (!isSidePanelOpen) {
        dispatch(openSidePanel(!isSidePanelOpen));
        dispatch(changePanelContent(SidePanelModes.GEOGEBRA));
      }
      dispatch(setGeogebraAppName(calculatorType));

      log(LogActionTypes.USER_CALCULATOR_MATH, {
        bookId: fullBook?.id,
      });
    },
    [isSidePanelOpen, geogebraAppName, dispatch, log, fullBook?.id]
  );

  useEffect(() => {
    dispatch(setToolboxVisible(true));
    return () => {
      dispatch(cleanPanel());
      dispatch(setStudentMode(false));
      dispatch(setCurrentStudent(undefined));
    };
    // eslint-disable-next-line
  }, []);

  const shouldShowDemoDownload = useMemo(() => {
    return (
      !hasSeenDownload &&
      (env === 'demo' || (env !== 'demo' && !isSchoolHours() && isTeacher)) &&
      showHelpModals
    );
  }, [hasSeenDownload, isTeacher]);

  const [initialStateNavBar, setInitialtateNavBar] = useState(hasSeenDownload);
  const [alertProps, setAlertProps] = useState<ShowAlert>(alertInitialState);

  useEffect(() => {
    if (initialStateNavBar === false && hasSeenDownload) {
      setAlertProps({
        showAlert: true,
        severity: 'success',
        message: 'We got it. Soon an improvement to make your teaching even better!!!',
      });
      setInitialtateNavBar(hasSeenDownload);
    }
  }, [hasSeenDownload, initialStateNavBar]);

  useEffect(
    () => () =>
      setAlertProps({
        showAlert: false,
        severity: 'error',
        message: '',
      }),
    []
  );

  const { exitFullScreen } = useFullScreen();
  const handleStartPresentationMode = useCallback(
    (closeOnly?: boolean) => {
      if (showPresentationMode && closeOnly) {
        return;
      }
      if (showPresentationMode) {
        exitFullScreen();
      } else {
        dispatch(
          setShowPresentationMode({
            showPresentationMode: !showPresentationMode,
            blocksToPresent: blocks ?? [],
          })
        );
      }
    },
    [blocks, dispatch, exitFullScreen, showPresentationMode]
  );

  useKeyBindings({
    F5: () => handleStartPresentationMode(true),
  });

  const handleToolboxAction = useCallback(
    (toolboxAction: ToolboxAction) => {
      switch (toolboxAction) {
        case ToolboxAction.PRINT:
          log(LogActionTypes.PRINT_TRAJECTORY_REQUEST, { trajectoryId });
          dispatch(setShowTrajectoryPrintPdfSlice(true));
          return;
        case ToolboxAction.SETTINGS:
          dispatch(changePanelContent(SidePanelModes.DIFFERENTIATION));
          dispatch(openSidePanel(!isSidePanelOpen));
          return;
        case ToolboxAction.BOOKMARK:
          setShowDialog(
            !hasSeenBookmark &&
              (env === 'demo' || (env !== 'demo' && !isSchoolHours() && isTeacher)) &&
              showHelpModals
          );

          if (bookmarkMode === BookmarkModeEnum.DISABLED) {
            let bookmarks: GetTeachersBookmarkByBook[] | GetStudentBookmark[] = [];
            if (isTeacher && teacherBookmarks) {
              bookmarks = teacherBookmarks;
            } else if (isStudent && studentBookmarksData) {
              bookmarks = studentBookmarksData;
            }
            if (bookmarks.length !== 0) {
              if (Array.isArray(studentBookmarksData)) {
                dispatch(
                  setScroll({
                    shouldScroll: true,
                    blockId: bookmarks[0].blockId,
                  })
                );
              }
            }
          }

          dispatch(
            setBookmarkMode(
              bookmarkMode === BookmarkModeEnum.DISABLED
                ? BookmarkModeEnum.SELECT_BLOCK
                : BookmarkModeEnum.DISABLED
            )
          );

          return;
        case ToolboxAction.GEOGEBRA_SCIENTIFIC:
          handleOpenSidePanelWithGeogebra(ToolboxAction.GEOGEBRA_SCIENTIFIC);
          return;
        case ToolboxAction.GEOGEBRA_GRAPHING:
          handleOpenSidePanelWithGeogebra(ToolboxAction.GEOGEBRA_GRAPHING);
          return;
        case ToolboxAction.GEOGEBRA_CAS:
          handleOpenSidePanelWithGeogebra(ToolboxAction.GEOGEBRA_CAS);
          return;
        case ToolboxAction.FOLLOW_UP:
          if (activeFilter !== StudentFiltersEnum.PROGRESS_AND_SCORES && isStudent) {
            dispatch(setActiveFilter(StudentFiltersEnum.PROGRESS_AND_SCORES));
            dispatch(setSelectedStudent(user.id));
          } else {
            dispatch(setActiveFilter(undefined));
            dispatch(setSelectedStudent(''));
          }
          return;
        case ToolboxAction.KNOWLEDGE_BASE:
          if (shouldShowDemoDownload) {
            log(LogActionTypes.DOWNLOAD_OPEN);
            return setShowDownloadDialog(true);
          }
          if (fullBook.knowledgeBaseUrl != null && fullBook.knowledgeBaseUrl !== '') {
            log(LogActionTypes.ACTIONS_TEACHER_LOBBY, {
              bookId: fullBook?.id,
              url: fullBook?.knowledgeBaseUrl,
            });
            return window.open(fullBook.knowledgeBaseUrl, '_blank');
          }
          return;
        case ToolboxAction.PRESENTATION_MODE:
          if (showPresentationMode) {
            log(LogActionTypes.TRAJECTORY_FULLSCREEN_DISABLE);
          } else {
            dispatch(setActiveFilter(undefined));
            dispatch(setSelectedGroup(''));
            dispatch(setSelectedStudent(''));
            log(LogActionTypes.TRAJECTORY_FULLSCREEN_ENABLE, {
              bookId,
              trajectoryId,
            });
          }

          return handleStartPresentationMode();
        case ToolboxAction.PRESENTATION_MODE_UP:
          return dispatch(setHandleUpArrow());
        case ToolboxAction.PRESENTATION_MODE_DOWN:
          return dispatch(setHandleDownArrow());
        case ToolboxAction.SOLUTIONS:
          dispatch(setSolutionsMode(!solutionsMode));
          return;
        case ToolboxAction.MANUAL:
          if (isToolboxPanelBlocked) return;
          if (isSidePanelOpen) {
            if (SidePanelModes.MANUAL) {
              log(LogActionTypes.PAPER_MANUAL_CLOSE, {
                trajectoryId,
                type: 'trajectory.button',
              });
            }
            dispatch(openSidePanel(false));
            dispatch(changePanelContent(SidePanelModes.MANUAL));
            return;
          }
          setShowManualDialog(
            !hasSeenManual &&
              (env === 'demo' || (env !== 'demo' && !isSchoolHours() && isTeacher)) &&
              showHelpModals
          );
          if (trajectory?.manual?.exists || isAuthor || isSuperAdmin) {
            log(LogActionTypes.PAPER_MANUAL_OPEN, { trajectoryId });
            dispatch(changePanelContent(SidePanelModes.MANUAL));
            dispatch(openSidePanel(true));
            return;
          }
          return;
        case ToolboxAction.NOTES:
          handleOpenSidePanelWithNotes();
          return;
      }
    },
    [
      log,
      trajectoryId,
      dispatch,
      isSidePanelOpen,
      hasSeenBookmark,
      isTeacher,
      bookmarkMode,
      handleOpenSidePanelWithGeogebra,
      activeFilter,
      isStudent,
      shouldShowDemoDownload,
      fullBook.knowledgeBaseUrl,
      fullBook?.id,
      showPresentationMode,
      handleStartPresentationMode,
      solutionsMode,
      isToolboxPanelBlocked,
      hasSeenManual,
      trajectory?.manual?.exists,
      isAuthor,
      isSuperAdmin,
      handleOpenSidePanelWithNotes,
      teacherBookmarks,
      studentBookmarksData,
      user.id,

      bookId,
    ]
  );

  window.onafterprint = () => {
    log(LogActionTypes.ACTIONS_PRINT);
  };

  const trajectoryToolboxTopButton: TrajectoryToolboxMenuProps[] = [
    {
      key: ToolboxAction.NOTES,

      icon: SpriteIconsLight.NOTEBOOK,
      roles: [UserRoles.STUDENT, UserRoles.TEACHER],
      tooltip: 'Notities',
      shouldRender:
        !isMobile && !(sectionCode === '2' && seriesId === undefined) && !showPresentationMode,
      id: 'trajectory-toolbox-notes',
      isBlockedForDemo: true,
    },
    {
      key: ToolboxAction.GEOGEBRA_SCIENTIFIC,
      type: ToolboxAction.GEOGEBRA_SCIENTIFIC,
      icon: SpriteIconsLight.SCIENTIFIC,
      roles: [UserRoles.STUDENT, UserRoles.TEACHER],
      tooltip: t('trajectory.geogebra.scientific'),
      shouldRender: isDesktop && fullBook.hasCalculatorScientific,
      active:
        toolboxPanelMode === ToolboxPanelModeType.GEOGEBRA &&
        toolboxPanelOpen &&
        geogebraAppName === ToolboxAction.GEOGEBRA_SCIENTIFIC,
    },
    {
      key: ToolboxAction.GEOGEBRA_GRAPHING,
      type: ToolboxAction.GEOGEBRA_GRAPHING,
      icon: SpriteIconsLight.GRAPHING,
      roles: [UserRoles.STUDENT, UserRoles.TEACHER],
      tooltip: t('trajectory.geogebra.graphing'),
      shouldRender: isDesktop && fullBook.hasCalculatorGraphical,
      active:
        toolboxPanelMode === ToolboxPanelModeType.GEOGEBRA &&
        toolboxPanelOpen &&
        geogebraAppName === ToolboxAction.GEOGEBRA_GRAPHING,
    },
    {
      key: ToolboxAction.GEOGEBRA_CAS,
      icon: SpriteIconsCustom.GEOGEBRA_CAS,
      roles: [UserRoles.STUDENT, UserRoles.TEACHER],
      tooltip: 'Algebraïsch rekentoestel',
      shouldRender: isDesktop && fullBook.hasAlgebraicCalculator,
      active:
        toolboxPanelMode === ToolboxPanelModeType.GEOGEBRA &&
        toolboxPanelOpen &&
        geogebraAppName === ToolboxAction.GEOGEBRA_CAS,
    },
    {
      key: ToolboxAction.BOOKMARK,
      icon: SpriteIconsLight.BOOKMARK,
      roles: [UserRoles.STUDENT, UserRoles.TEACHER],
      tooltip: t('shared.bookmark'),
      shouldRender: !isMobile && !showPresentationMode,
      active: bookmarkMode === BookmarkModeEnum.SELECT_BLOCK,
      isBlockedForDemo: true,
    },
    {
      key: ToolboxAction.FOLLOW_UP,
      icon: SpriteIconsLight.PROGRESS_AND_SCORES,
      tooltip: t('followUp.title'),
      roles: [UserRoles.STUDENT],
      shouldRender: !isMobile && !showPresentationMode,
      active: activeFilter && activeFilterIsStudentFilter(activeFilter, 'trajectory'),
    },
    {
      key: ToolboxAction.SOLUTIONS,
      icon: SpriteIconsLight.CHECK,
      tooltip: t('trajectory.sections.shared.solutions.showSolutions'),
      roles: [UserRoles.TEACHER],
      active: solutionsMode,
      shouldRender: !isMobile,
      isBlockedForDemo: true,
    },
  ];

  const trajectoryToolboxBottomButton: TrajectoryToolboxMenuProps[] = [
    {
      key: ToolboxAction.SETTINGS,
      icon: SpriteIconsLight.SETTINGS,
      roles: [UserRoles.AUTHOR, UserRoles.TEACHER],
      tooltip: t('shared.settings'),
      shouldRender: !isMobile && !showPresentationMode,
      //className: 'hs-cta-trigger-button hs-cta-trigger-button-85218745545',
      isBlockedForDemo: true,
    },
    {
      key: ToolboxAction.MANUAL,
      icon: SpriteIconsLight.OPEN_BOOK,
      roles: [UserRoles.TEACHER, UserRoles.AUTHOR, UserRoles.SUPER_ADMIN],
      tooltip: t('shared.manual'),
      shouldRender:
        !showPresentationMode &&
        ((!showPresentationMode &&
          !isMobile &&
          (trajectory?.manual?.exists || isAuthor || isSuperAdmin)) ||
          (!hasSeenManual && env === 'demo') ||
          ((!hasSeenManual || !!trajectory?.manual?.exists) &&
            env !== 'demo' &&
            !isSchoolHours() &&
            isTeacher)),
      active: toolboxPanelOpen && toolboxPanelContent?.type === ToolboxPanelTypes.MANUAL,
    },
    {
      key: ToolboxAction.KNOWLEDGE_BASE,
      icon: SpriteIconsLight.CLOUD_ARROW_DOWN,
      tooltip: t('shared.knowledgeBase'),
      roles: [UserRoles.TEACHER],
      badge: shouldShowDemoDownload
        ? {
            badgeContent: '!',
            color: 'secondary',
          }
        : null,
      shouldRender: !isMobile && !showPresentationMode,
      isBlockedForDemo: true,
    },
    {
      key: ToolboxAction.PRINT,
      icon: SpriteIconsLight.PRINT,
      roles: [
        UserRoles.STUDENT,
        UserRoles.TEACHER,
        UserRoles.ADMIN,
        UserRoles.AUTHOR,
        UserRoles.SUPER_ADMIN,
      ],
      tooltip: t('shared.print'),
      shouldRender: !isMobile && !showPresentationMode && ['0', '1'].includes(currentSectionCode!),
    },
    {
      key: ToolboxAction.PRESENTATION_MODE,
      roles: [
        UserRoles.STUDENT,
        UserRoles.TEACHER,
        UserRoles.ADMIN,
        UserRoles.AUTHOR,
        UserRoles.SUPER_ADMIN,
      ],
      icon: showPresentationMode ? SpriteIconsLight.STOP : SpriteIconsLight.PLAY,
      tooltip: showPresentationMode
        ? t('shared.fullscreenDeactivate')
        : t('shared.fullscreenActivate'),
      shouldRender: !isMobile && ['0', '1'].includes(currentSectionCode!),
    },
    {
      key: ToolboxAction.PRESENTATION_MODE_UP,
      roles: [
        UserRoles.STUDENT,
        UserRoles.TEACHER,
        UserRoles.ADMIN,
        UserRoles.AUTHOR,
        UserRoles.SUPER_ADMIN,
      ],
      icon: SpriteIconsRegular.CHEVRONS_UP,
      tooltip: 'Naar boven',
      shouldRender: !isMobile && showPresentationMode,
    },
    {
      key: ToolboxAction.PRESENTATION_MODE_DOWN,
      roles: [
        UserRoles.STUDENT,
        UserRoles.TEACHER,
        UserRoles.ADMIN,
        UserRoles.AUTHOR,
        UserRoles.SUPER_ADMIN,
      ],
      icon: SpriteIconsRegular.CHEVRONS_DOWN,
      tooltip: 'Naar onder',
      shouldRender: !isMobile && showPresentationMode,
    },
  ];

  return (
    <>
      <TrajectoryToolboxWrapper
        id="trajectory-toolbox-wrapper"
        sidepanelwidth={sidepanel?.width}
        presentationmode={showPresentationMode}
        device={deviceType}
        className={`hide-on-print ${fullscreenMode ? 'fullscreen' : ''} ${
          toolboxPanelMode === ToolboxPanelModeType.NOTES && bookId ? 'notes-panel-active' : ''
        } ${isSidePanelOpen ? 'side-panel-active' : ''}`}
        style={{ right: isSidePanelOpen ? sidepanel?.width : 0 }}
      >
        {[SidePanelModes.NOTES, SidePanelModes.GROUP_NOTES].includes(
          sidePanelMode as SidePanelModes
        ) && bookId ? (
          <TrajectoryToolboxNotesPanel bookId={bookId} pageId={pageId} />
        ) : (
          <>
            <TrajectoryToolboxMenu>
              {activeFilter === StudentFiltersEnum.PROGRESS_AND_SCORES && <TrajectoryControls />}
              {trajectoryToolboxTopButton.map((button) => {
                return button?.shouldRender &&
                  button.roles.some((buttonRole) =>
                    user.roles.some((userRole) => userRole === buttonRole)
                  ) ? (
                  <TrajectoryToolboxButton
                    {...button}
                    type={button.key}
                    onClick={() => handleToolboxAction(button.key)}
                  />
                ) : null;
              })}
            </TrajectoryToolboxMenu>
            <TrajectoryToolboxMenu>
              {trajectoryToolboxBottomButton.map((button) => {
                return button?.shouldRender &&
                  button.roles.some((buttonRole) =>
                    user.roles.some((userRole) => userRole === buttonRole)
                  ) ? (
                  <TrajectoryToolboxButton
                    {...button}
                    type={button.key}
                    onClick={() => handleToolboxAction(button.key)}
                  />
                ) : null;
              })}
            </TrajectoryToolboxMenu>
          </>
        )}
      </TrajectoryToolboxWrapper>
      <DemoSnackBar {...alertProps} />
      {showTrajectoryPrintPdfSlice && <PrintPdf />}
      {isTeacher && (
        <DemoDialog
          showDialog={showDownloadDialog}
          onClose={() => setShowDownloadDialog(false)}
          props={downloadModalProps}
        />
      )}
      {isTeacher && (
        <DemoDialog
          showDialog={showDialog}
          onClose={() => setShowDialog(false)}
          props={bookmarkModalProps}
        />
      )}
      {isTeacher && (
        <DemoDialog
          showDialog={showManualDialog}
          onClose={() => setShowManualDialog(false)}
          props={teachersManualkModalProps}
        />
      )}
    </>
  );
};

export default TrajectoryToolbox;
